import DOMPurify from 'dompurify';
import { postBoomiService, postSegmentService } from '../../services/trackingServices';
import { gtagEnvelope } from '../gtagEnvelope';
import { FormData, SendAsyncPostObject, SitecoreTrialContentType } from '../../types';
import { getDataResidencyParam } from '../getDataResidencyParam';
import { getCookie } from '../cookies';
import { emailCheck } from '../emailCheck';
import { postTrialFormDataService, retainFormData } from '../../services/sitecoreServices';
import { getFlowType, getFlowTypePayload } from '../getFlowType';
import { FORM_TYPES, TRIAL_TYPES } from '../../consts';
import { getEventName, getEventReason } from '../getExperimentVersion';
import { facebookCAPIGTMEvent } from '../facebookCAPIGTMEvent';
import { hashEncodeUserDataField } from '../hashEncodeUserDataField';
import { analyticsEvent } from '../analyticsEvent';

interface StandardSubmission {
  data: SitecoreTrialContentType;
  existingUserEmail: string;
  values: SendAsyncPostObject;
  marketingOptOutSeen: boolean;
  contactPermission: boolean;
}

export const handleStandardTrialSubmission = async ({
  data,
  existingUserEmail,
  values,
  marketingOptOutSeen,
  contactPermission,
}: StandardSubmission) => {
  const { formType, trialLength, segExpQuery, segExpValue, isLoggedIn, trialType } = window.LPFormData;

  const flowType = getFlowType(formType);

  const is2Step = trialType === TRIAL_TYPES.BUSINESS && flowType !== FORM_TYPES.PURCHASE && !existingUserEmail;

  try {
    gtagEnvelope('event', 'cta_submit_click', {
      cta_name: `Start My Free ${trialLength}-Day Trial`,
      cta_url: `${window.location.origin}${window.location.pathname}${data.successUrl}`,
      cta_position: 'Trial Form',
    });

    await postSegmentService({
      event: `${getEventName(true)}_trial_form_click`,
      properties: {
        reason: getEventReason(),
        ...(is2Step && { journey_step: `Step 2` }),
      },
    });

    const userEmail = existingUserEmail || DOMPurify.sanitize(values.email.toString().toLowerCase());

    let fullName = values.fullName.toString();
    let [firstName, ...rest] = fullName.split(' ');
    let lastName = rest.join(' ');

    // gather form data
    const formData: FormData = {
      email: userEmail,
      firstName: DOMPurify.sanitize(firstName),
      lastName: DOMPurify.sanitize(lastName),
      jobTitle: values.title && DOMPurify.sanitize(values.title.toString()),
      companyName: DOMPurify.sanitize(values.company.toString()),
      companySize: values.employees && values.employees.toString(),
      phoneNumber: values.temp_phone__c && DOMPurify.sanitize(values.temp_phone__c.toString()),
      trialSku: data.sku,
      marketingOptOutShown: marketingOptOutSeen,
      contactPermission,
      clientIp: DOMPurify.sanitize(window.akCIp),
      language: DOMPurify.sanitize(getCookie('lang')) || 'en_US',
      trialLength: trialLength || 14,
      dataResidency: getDataResidencyParam(),
      flowType: getFlowTypePayload(formType),
      formType,
      affiliateClickId: DOMPurify.sanitize(getCookie('clickid')),
      segExpQuery,
      segExpValue: segExpValue,
    };

    if (!existingUserEmail) {
      const bossInvalid = await emailCheck(userEmail);
      if (!bossInvalid.success) {
        if (bossInvalid.code === 2006) {
          retainFormData(formData);
        }
        return { success: false, code: bossInvalid.code };
      }
    }

    const resp = await postTrialFormDataService(formData);
    if (!resp.success) {
      if (resp.code === 2006) {
        retainFormData(formData);
      }
      return { success: false, code: resp.code };
    } else {
      gtagEnvelope('event', 'trial_success_business', {});
      facebookCAPIGTMEvent({
        contentId: 'lastpass_business',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phoneNumber,
      });
      await analyticsEvent(formData.email);
      if (flowType !== FORM_TYPES.PURCHASE) {
        formData.programName = data.inputs?.programName?.defaultValue;
        formData.programID = data.inputs?.programID?.defaultValue;
        await postBoomiService({
          data: formData,
          userKey: resp.successToken,
        });

        if (isLoggedIn) {
          window.location.href = data!.successLoggedInUrl;
        } else {
          window.location.href = `${data.successUrl}?email=${formData.email}`;
        }
      } else {
        window.location.href = data.successUrl;
      }
    }
    return { success: true };
  } catch (error) {
    return {
      success: false,
      code: '500',
      errorMessage: `Trial submit error: ${error.message}`,
    };
  }
};
